import { TextInput } from '@mcgames/ui';
import { useState, type KeyboardEvent } from 'react';
import styles from './Games.module.css';

export type TagInputProps = {
  legend: string;
  inputLabel: string;
  hint: string;
  removeLabel: (tag: string) => string;
  value: string[];
  onChange: (tags: string[]) => void;
};

/** A list of removable tags; Enter, a comma or leaving the field adds the typed text as a tag. */
export function TagInput({
  legend,
  inputLabel,
  hint,
  removeLabel,
  value,
  onChange,
}: TagInputProps) {
  const [draft, setDraft] = useState('');

  function commit() {
    const tag = draft.trim();
    setDraft('');
    if (tag === '') return;
    const known = value.some((existing) => existing.toLowerCase() === tag.toLowerCase());
    if (!known) onChange([...value, tag]);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter' || event.key === ',') {
      // Enter must not submit the surrounding form.
      event.preventDefault();
      commit();
    }
  }

  return (
    <fieldset className={styles['fieldset']}>
      <legend>{legend}</legend>
      {value.length > 0 && (
        <ul className={`${styles['chips']} ${styles['tagList']}`}>
          {value.map((tag) => (
            <li key={tag} className={styles['tag']}>
              {tag}
              <button
                type="button"
                className={styles['tagRemove']}
                aria-label={removeLabel(tag)}
                onClick={() => onChange(value.filter((existing) => existing !== tag))}
              >
                <span aria-hidden="true">×</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <TextInput
        label={inputLabel}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
      />
      <span className={styles['hint']}>{hint}</span>
    </fieldset>
  );
}
